import { useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSubmitOnChange } from '../../UI';
import { NoteReceivableFormSubmitData, NoteReceivableFormValues } from '../types';
import { getNoteReceivableFormDefaultValues } from '../util';
import { NoteReceivable } from '../../generated/graphql';
import { useNoteReceivable } from '../queries';

export interface UseNoteReceivableFormArgs {
  householdID: string;
  saveOnChange?: boolean;
  noteReceivableID?: string | null;
  defaultValueOverrides?: Partial<NoteReceivableFormValues>;
  handleSubmit?: (data: NoteReceivableFormSubmitData) => void;
  noteReceivable?: NoteReceivable | null;
  isLoading?: boolean;
}

export const useNoteReceivableForm = ({
  householdID,
  saveOnChange,
  handleSubmit,
  noteReceivableID,
  defaultValueOverrides,
  noteReceivable: noteReceivableProp,
  isLoading = false,
}: UseNoteReceivableFormArgs) => {
  const { noteReceivable: noteReceivableFromQuery, noteReceivableQuery } = useNoteReceivable({
    householdID,
    id: noteReceivableID ?? '',
  });

  const noteReceivable = noteReceivableProp ?? noteReceivableFromQuery;
  const isLoadingFormData = noteReceivableQuery.isLoading || isLoading;
  const formReady = !isLoadingFormData && (noteReceivable || !noteReceivableID);

  const formMethods = useForm<NoteReceivableFormValues>({
    // @ts-expect-error TODO: resolve type narrowing/widening with form values to support async entity loading
    values: getNoteReceivableFormDefaultValues(noteReceivable, defaultValueOverrides),
  });

  const onSubmit: SubmitHandler<NoteReceivableFormValues> = useCallback(
    (data) => {
      handleSubmit?.({
        formValues: data,
        householdID,
        changeToken: noteReceivable?.changeToken,
        noteReceivableID: noteReceivableID || undefined,
        valueDataPointGroupID: noteReceivable?.value.id || '',
      });
    },
    [noteReceivableID, handleSubmit, householdID, noteReceivable?.changeToken, noteReceivable?.value.id]
  );

  useSubmitOnChange({
    onChange: onSubmit,
    watch: formMethods.watch,
    enabled: saveOnChange && !!noteReceivable,
    handleSubmit: formMethods.handleSubmit,
  });

  return { formMethods, onSubmit, isLoadingFormData, formReady, noteReceivable, householdID };
};
